import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

const BaseModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  elevated = false,
  portal = false,
  // Optional custom header (replaces the title row) and body padding override.
  header,
  bodyClassName = "p-4 sm:p-6",
}) => {
  // Mobile: full width. Laptop/desktop (sm and up): original modal dimensions.
  const sizes = {
    sm: "max-w-full sm:max-w-md",
    md: "max-w-full sm:max-w-lg",
    lg: "max-w-full sm:max-w-2xl",
    xl: "max-w-full sm:max-w-4xl",
    "2xl": "max-w-full sm:max-w-6xl",
    full: "max-w-full mx-2 sm:mx-4",
  };

  // Elevated = confirm/dialog on top of another modal (e.g. delete confirm over edit task)
  const backdropZ = elevated ? 99990 : 100;
  const contentZ = elevated ? 99991 : 110;

  const content = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop - elevated modals use higher z-index (inline so it always applies) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={onClose}
            className="fixed inset-0 bg-overlay backdrop-blur-md"
            style={{ zIndex: backdropZ }}
          />

          {/* Modal: full width on mobile, normal size on desktop (sm and up) */}
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ zIndex: contentZ }}
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0, y: 14 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.97, opacity: 0, y: 14 }}
              transition={{ duration: 0.28, ease: [0.2, 0.9, 0.3, 1] }}
              role="dialog"
              aria-modal="true"
              className={`bg-panel-solid rounded-t-3xl sm:rounded-3xl shadow-modal w-full ${sizes[size]} max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-line2 relative`}
            >
              {header ? (
                <div className="relative z-10 shrink-0">{header}</div>
              ) : (
              <div className="flex items-start justify-between px-4 py-4 sm:px-6 sm:py-5 border-b border-line relative z-10 shrink-0">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-w-0"
                >
                  <h2 className="font-display text-[20px] font-semibold tracking-[-0.02em] text-ink">
                    {title}
                  </h2>
                  {subtitle && (
                    <p className="text-[13.5px] text-muted mt-1">
                      {subtitle}
                    </p>
                  )}
                </motion.div>
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.15 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={onClose}
                  aria-label="Close"
                  className="text-muted hover:text-ink transition-colors p-2 rounded-xl border border-line2 hover:bg-hover"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </motion.button>
              </div>
              )}

              {/* Body */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className={`flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin relative z-10 min-h-0 ${bodyClassName}`}
              >
                {children}
              </motion.div>

              {/* Footer */}
              {footer && (
                <div className="flex flex-wrap items-center justify-end gap-3 px-4 py-3.5 sm:px-6 sm:py-4 border-t border-line relative z-10 shrink-0">
                  {footer}
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  if (portal) {
    return createPortal(content, document.body);
  }
  return content;
};

export default BaseModal;
